import { useQuery } from '@apollo/client'
import { Autocomplete, TextField, type AutocompleteProps } from '@mui/material'
import {
    CandidateFilterCategory,
    SearchIndexField,
    type GetSectorOptionsQuery,
} from '__generated__/graphql'
import { useMemo, useState } from 'react'
import { FilterWrapper } from 'recruiter/components/FilterWrapper'
import { GetSectorOptions } from 'recruiter/graphql/companies.gql'
import { useRosterSearchFilter } from 'recruiter/hooks'

export function RosterSectorFilter() {
    const { value, setValue } = useRosterSearchFilter(
        SearchIndexField.RosterSectors
    )
    const [sectorOptions, setSectorOptions] = useState<
        GetSectorOptionsQuery['sectors']
    >([])
    const [open, setOpen] = useState(false)

    const { loading: areSectorOptionsLoading } = useQuery(GetSectorOptions, {
        onCompleted(data) {
            setSectorOptions(data?.sectors ?? [])
        },
    })

    const sectorsInFilter = useMemo(() => {
        const sectorIdsInFilter = value?.value?.stringArray ?? []
        return sectorOptions.filter(({ id }) => sectorIdsInFilter.includes(id))
    }, [sectorOptions, value])

    const handleSectorChange: AutocompleteProps<
        (typeof sectorOptions)[number],
        true,
        false,
        false
    >['onChange'] = (_, newValues) => {
        if (newValues.length === 0) {
            setValue(null)
            return
        }

        setValue({
            category: CandidateFilterCategory.All,
            value: { stringArray: newValues.map(({ id }) => id) },
        })
    }

    return (
        <FilterWrapper label="Sector">
            <Autocomplete
                ChipProps={{ size: 'small' }}
                loading={areSectorOptionsLoading}
                multiple
                open={open}
                onOpen={() => setOpen(true)}
                onClose={(_, reason) => {
                    if (reason === 'blur') {
                        setOpen(false)
                    }
                }}
                onChange={handleSectorChange}
                getOptionLabel={({ value }) => value}
                getOptionKey={({ id }) => id}
                options={sectorOptions}
                renderInput={(params) => (
                    <TextField {...params} placeholder="Search sectors..." />
                )}
                value={sectorsInFilter}
            />
        </FilterWrapper>
    )
}
